import React, {useEffect, useState} from "react";
import {Button, DatePicker, Form, InputNumber, Modal, Radio, Select} from "antd";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {useStore} from "../../../../hooks/useStore";

export const UpdatePoolForecastModal = ({
    poolId,
    shiftHours = [],
    data,
    visible,
    setVisible,
    recall = () => {}
}) => {
    const stores = useStore();
    const [throughput, setThroughput] = useState(0);
    const [forecast, setForecast] = useState(0);
    const [referenceStartDate, setReferenceStartDate] = useState(null);
    const [referenceEndDate, setReferenceEndDate] = useState(null);
    const [shiftStartHourId, setShiftStartHourId] = useState(null);
    const [shiftEndHourId, setShiftEndHourId] = useState(null);
    const [ordersDayType, setOrdersDayType] = useState("sameDay");
    const [date, setDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const {t} = useTranslation("pools");

    const initForecast = () => {
        setThroughput(data?.throughput || 0);
        setForecast(data?.forecast || 0);
        setReferenceStartDate(
            (data?.referenceStartDate && moment(data?.referenceStartDate)) || null
        );
        setReferenceEndDate((data?.referenceEndDate && moment(data?.referenceEndDate)) || null);
        setShiftStartHourId(data?.shiftStartHourId || null);
        setShiftEndHourId(data?.shiftEndHourId || null);
        setOrdersDayType(data?.ordersDayType || "sameDay");
        setDate((data?.date && moment(data?.date)) || null);
    };

    const validate = () => {
        const tmpErrors = {};
        if (!date) {
            tmpErrors.date = "Tarih alanı boş bırakılamaz";
        }
        if (!throughput) {
            tmpErrors.throughput = "Throughput alanı boş bırakılamaz";
        }
        if (!forecast) {
            tmpErrors.forecast = "Forecast alanı boş bırakılamaz";
        }
        if (!referenceStartDate) {
            tmpErrors.referenceDate = "Referans Tarih Aralığı alanı boş bırakılamaz";
        }
        if (!referenceEndDate) {
            tmpErrors.referenceDate = "Referans Tarih Aralığı alanı boş bırakılamaz";
        }
        if (!shiftStartHourId) {
            tmpErrors.shiftStartHourId = "Başlangıç Saati alanı boş bırakılamaz";
        }
        if (!shiftEndHourId) {
            tmpErrors.shiftEndHourId = "Bitiş Saati alanı boş bırakılamaz";
        }
        setErrors(tmpErrors);
        return Object.keys(tmpErrors).length === 0;
    };

    const handleUpdatePoolForecast = async () => {
        setLoading(true);
        if (!validate()) {
            setLoading(false);
            return;
        }
        try {
            const payload = {
                poolId,
                date: date?.format("YYYY-MM-DD"),
                throughput,
                forecast,
                referenceStartDate: referenceStartDate?.format("YYYY-MM-DD"),
                referenceEndDate: referenceEndDate?.format("YYYY-MM-DD"),
                shiftStartHourId,
                shiftEndHourId,
                ordersDayType
            };
            const isRequestSuccess = await stores.poolShiftStore.setDailyForecast(payload);
            if (isRequestSuccess) {
                setVisible(false);
                recall();
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            initForecast();
        }
    }, [visible]);

    return (
        <Modal
            aria-modal="true"
            alt="updatePoolForecastModal"
            title={"Forecast Güncelle"}
            visible={visible}
            onCancel={() => setVisible(false)}
            footer={[
                <Button key="back" onClick={() => setVisible(false)}>
                    İptal
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleUpdatePoolForecast}
                >
                    Güncelle
                </Button>
            ]}
        >
            <Form layout="vertical" alt="updatePoolForecastForm">
                <Form.Item
                    label={"Tarih"}
                    validateStatus={errors?.date ? "error" : "success"}
                    help={errors?.date ? errors?.date : null}
                >
                    <DatePicker
                        data-testid="date"
                        value={date}
                        onChange={value => setDate(value)}
                        disabledDate={currentDate =>
                            currentDate && currentDate < moment().startOf("day")
                        }
                    />
                </Form.Item>

                <Form.Item
                    label={"Throughput"}
                    validateStatus={errors?.throughput ? "error" : "success"}
                    help={errors?.throughput ? errors?.throughput : null}
                >
                    <InputNumber
                        data-testid="throughput"
                        value={throughput}
                        onChange={value => setThroughput(value)}
                    />
                </Form.Item>
                <Form.Item
                    label={"Sipariş Tahmini"}
                    validateStatus={errors?.forecast ? "error" : "success"}
                    help={errors?.forecast ? errors?.forecast : null}
                >
                    <InputNumber
                        data-testid="forecast"
                        value={forecast}
                        onChange={value => setForecast(value)}
                    />
                </Form.Item>
                <Form.Item
                    label={"Referans Tarih Aralığı"}
                    validateStatus={errors?.referenceDate ? "error" : "success"}
                    help={errors?.referenceDate ? errors?.referenceDate : null}
                >
                    <DatePicker.RangePicker
                        data-testid="referenceDate"
                        value={[referenceStartDate, referenceEndDate]}
                        onChange={value => {
                            setReferenceStartDate(value[0]);
                            setReferenceEndDate(value[1]);
                        }}
                        disabledDate={currentDate =>
                            currentDate &&
                            currentDate >
                                moment()
                                    .add(1, "day")
                                    .startOf("day")
                        }
                    />
                </Form.Item>
                <Form.Item
                    label={"Başlangıç Saati"}
                    validateStatus={errors?.shiftStartHourId ? "error" : "success"}
                    help={errors?.shiftStartHourId ? errors?.shiftStartHourId : null}
                >
                    <Select
                        data-testid="shiftStartHourId"
                        value={shiftStartHourId}
                        onChange={value => setShiftStartHourId(value)}
                    >
                        {shiftHours?.map(shiftHour => (
                            <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                {`${shiftHour?.startTime}`}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={"Bitiş Saati"}
                    validateStatus={errors?.shiftEndHourId ? "error" : "success"}
                    help={errors?.shiftEndHourId ? errors?.shiftEndHourId : null}
                >
                    <Select
                        data-testid="shiftEndHourId"
                        value={shiftEndHourId}
                        onChange={value => setShiftEndHourId(value)}
                    >
                        {shiftHours?.map(shiftHour => (
                            <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                {`${shiftHour?.endTime}`}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={"Gün Tipi"}
                    validateStatus={errors?.ordersDayType ? "error" : "success"}
                    help={errors?.ordersDayType ? errors?.ordersDayType : null}
                >
                    <Radio.Group
                        data-testid="ordersDayType"
                        value={ordersDayType}
                        onChange={event => setOrdersDayType(event.target.value)}
                    >
                        <Radio value="sameDay">Same Day</Radio>
                        <Radio value="allDays">All Days</Radio>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
};
